import { useEffect, useState } from "react";
import { B, fonts, Btn, Card } from "../../../components/UI";

function SettingSwitch({ label, hint, checked, onChange, disabled }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        padding: "14px 0",
        borderBottom: `1px solid ${B.borderLight}`,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: B.text, fontFamily: fonts.body }}>{label}</div>
        <div style={{ fontSize: 12, color: B.textMid, marginTop: 2, fontFamily: fonts.body }}>{hint}</div>
      </div>

      <div
        style={{
          width: 46,
          height: 26,
          borderRadius: 999,
          background: checked ? B.orange : "#cbd5e1",
          padding: 3,
          transition: "background 0.2s",
          position: "relative",
          flexShrink: 0,
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        />
        <span
          style={{
            display: "block",
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            transform: checked ? "translateX(20px)" : "translateX(0)",
            transition: "transform 0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </div>
    </label>
  );
}

function AmountField({ label, hint, value, onChange, disabled }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        padding: "14px 0",
        borderBottom: `1px solid ${B.borderLight}`,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: B.text, fontFamily: fonts.body }}>{label}</div>
        <div style={{ fontSize: 12, color: B.textMid, marginTop: 2, fontFamily: fonts.body }}>{hint}</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: B.textMid, fontFamily: fonts.body }}>₹</span>
        <input
          type="number"
          min="0"
          step="1"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 110, background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: B.text, fontFamily: fonts.body, outline: "none", textAlign: "right" }}
        />
      </div>
    </label>
  );
}

function PercentField({ label, hint, value, onChange, disabled }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        padding: "14px 0",
        borderBottom: `1px solid ${B.borderLight}`,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: B.text, fontFamily: fonts.body }}>{label}</div>
        <div style={{ fontSize: 12, color: B.textMid, marginTop: 2, fontFamily: fonts.body }}>{hint}</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 90, background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: B.text, fontFamily: fonts.body, outline: "none", textAlign: "right" }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, color: B.textMid, fontFamily: fonts.body }}>%</span>
      </div>
    </label>
  );
}

export default function SettingsTab({ quotationSettings, onSave, saving }) {
  const [form, setForm] = useState(quotationSettings);

  useEffect(() => {
    setForm(quotationSettings);
  }, [quotationSettings]);

  return (
    <div>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Manage site-level quotation permissions from one place.</p>
      <Card>
        <SettingSwitch
          label="Allow price edits in quotation"
          hint="When enabled, admins can override item price while creating or editing quotations."
          checked={Boolean(form.allowPriceEdit)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, allowPriceEdit: checked }))}
        />
        <SettingSwitch
          label="Allow discount in quotation"
          hint="When enabled, discount can be added on total while creating or editing quotations. Applies only to quotations, not invoices."
          checked={Boolean(form.allowDiscount)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, allowDiscount: checked }))}
        />
        <PercentField
          label="Default discount % for quotation"
          hint="When the quotation discount setting above is enabled, this percentage is auto-applied on new quotations. It can still be changed per quotation."
          value={form.defaultDiscountQuotation ?? ""}
          disabled={saving}
          onChange={(value) => setForm((prev) => ({ ...prev, defaultDiscountQuotation: value }))}
        />
        <AmountField
          label="Minimum order value for quotation auto discount"
          hint="The auto-applied quotation discount is only applied when the quotation item total reaches this value. Leave 0 for no minimum."
          value={form.minOrderValueQuotation ?? ""}
          disabled={saving}
          onChange={(value) => setForm((prev) => ({ ...prev, minOrderValueQuotation: value }))}
        />
        <SettingSwitch
          label="Allow discount in invoice"
          hint="When enabled, discount can be added on total while creating or editing invoices (documents with a bill number). Applies only to invoices, not quotations."
          checked={Boolean(form.allowDiscountInvoice)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, allowDiscountInvoice: checked }))}
        />
        <PercentField
          label="Default discount % for invoice"
          hint="When the invoice discount setting above is enabled, this percentage is auto-applied once a bill number is entered. It can still be changed per invoice."
          value={form.defaultDiscountInvoice ?? ""}
          disabled={saving}
          onChange={(value) => setForm((prev) => ({ ...prev, defaultDiscountInvoice: value }))}
        />
        <AmountField
          label="Minimum order value for invoice auto discount"
          hint="The auto-applied invoice discount is only applied when the invoice item total reaches this value. Leave 0 for no minimum."
          value={form.minOrderValueInvoice ?? ""}
          disabled={saving}
          onChange={(value) => setForm((prev) => ({ ...prev, minOrderValueInvoice: value }))}
        />
        <SettingSwitch
          label="Auto-send quotation on WhatsApp"
          hint="When enabled, the 'Send on WhatsApp' checkbox will be checked by default when creating quotations."
          checked={Boolean(form.autoSendWhatsApp)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, autoSendWhatsApp: checked }))}
        />
        <SettingSwitch
          label="Show Firm Name"
          hint="When enabled, the Firm Name field is shown in customer forms, customer lists, details and quotations."
          checked={Boolean(form.showFirmName)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, showFirmName: checked }))}
        />
        <SettingSwitch
          label="Show Packaging Charges"
          hint="When enabled, packaging charges can be added and shown on quotations while creating or editing them."
          checked={Boolean(form.showPackagingCharges)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, showPackagingCharges: checked }))}
        />
        <SettingSwitch
          label="Auto-fetch design info in quotation"
          hint="When enabled, entering an existing design number in a quotation auto-fetches its image & price from the database. When disabled, design numbers are added manually and no design catalogue is loaded — saving database reads."
          checked={Boolean(form.fetchDesignDetails)}
          disabled={saving}
          onChange={(checked) => setForm((prev) => ({ ...prev, fetchDesignDetails: checked }))}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Btn onClick={() => onSave(form)} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </Btn>
        </div>
      </Card>
    </div>
  );
}
